import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LocalizedLink from "@/components/LocalizedLink";
import { getPathLanguage, withLanguagePrefix } from "@/lib/i18n";
import { useAuthStore } from "@/store";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const currentLanguage = getPathLanguage(location.pathname);
  const queryRedirect = new URLSearchParams(location.search).get("redirect");
  const requestedRedirect = location.state?.redirect || queryRedirect;
  const redirectTo = requestedRedirect?.startsWith("/") && !requestedRedirect.startsWith("//")
    ? requestedRedirect
    : "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    const result = await login(email, password);
    if (result.success) {
      toast.success("Welcome back!");
      const localizedRedirect = withLanguagePrefix(redirectTo, currentLanguage);
      navigate(localizedRedirect);
    } else {
      toast.error(result.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md">
          <LocalizedLink to="/" className="flex items-center gap-2 mb-8">
            <img 
              src="/assets/logo-creavideos.png" 
              alt="CreaVideos" 
              className="h-10 w-auto"
            />
          </LocalizedLink>

          <h1 className="text-3xl font-bold mb-2">Welcome back</h1>
          <p className="text-slate-600 mb-8">
            Log in to your account to continue creating
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <LocalizedLink to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700">
                  Forgot password?
                </LocalizedLink>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 btn-primary rounded-full"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                "Log in"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-600">
            Don't have an account?{" "}
            <LocalizedLink to="/register" className="text-teal-600 font-medium hover:text-teal-700">
              Sign up
            </LocalizedLink>
          </p>
      </div>
    </div>
  );
}
